import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
} from 'react'
import {
  acceptReview,
  completeBeatMap,
  displayChord,
  formatKey,
  isKeyEstimated,
  meterOf,
  needsReview,
  setCapo,
  shapeKeyOf,
  timingOf,
  transposeContent,
  type EventRef,
  type Fragment,
  type Practice,
  type PracticePatch,
  type SongDocument,
} from '../../domain'
import {
  keepAwakeMethod,
  startKeepAwake,
  stopKeepAwake,
  subscribeKeepAwake,
} from '../../platform/keepAwake'
import { saveDetail, takeResumeDetail } from '../../platform/session'
import { getDb } from '../../storage/db'
import { actionForKey, keyId, type PedalAction } from '../../storage/preferences'
import {
  getPractice,
  getSongDocument,
  saveRevision,
  updatePractice,
} from '../../storage/repository'
import { MAX_BPM } from '../../transport/tap'
import { normalizeLoop, type Loop } from '../../transport/transport'
import common from '../common.module.css'
import { useLive } from '../hooks/useLive'
import { ChordCard } from '../play/ChordCard'
import {
  buildEvents,
  firstEventOfOccurrences,
  indexForPick,
  jumpSection,
  linkEvents,
  nextDistinct,
} from '../play/events'
import styles from '../play/Play.module.css'
import { PracticePanel } from '../play/PracticePanel'
import { sectionJumpLabel } from '../play/sectionLabel'
import { Sheet, type SheetPosition } from '../play/Sheet'
import transportStyles from '../play/Transport.module.css'
import { TransportBar } from '../play/TransportBar'
import { TunePanel } from '../play/TunePanel'
import { useTransport } from '../play/useTransport'
import { updatePreferences, usePreferences } from '../prefs'
import { paths } from '../router'
import { t } from '../strings'

const MIN_SIZE = 16
const MAX_SIZE = 64
const NOTICE_MS = 4000
/** Tempo de práctica si la ficha no trae ninguno. */
const DEFAULT_BPM = 80

const number = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

/** Los campos de texto no deben recibir las teclas del pedal. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

export function Play({ arrangementId }: { arrangementId: string }) {
  const { data: doc, loading } = useLive(
    () => getSongDocument(getDb(), arrangementId),
    [arrangementId],
  )
  const { data: practice, loading: practiceLoading } = useLive(
    () => getPractice(getDb(), arrangementId),
    [arrangementId],
  )
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (notice === null) return
    const timer = setTimeout(() => setNotice(null), NOTICE_MS)
    return () => clearTimeout(timer)
  }, [notice])

  if (loading || practiceLoading) return <p className={common.muted}>{t.play.loading}</p>
  if (!doc) {
    return (
      <div className={common.page}>
        <p className={common.noticeError}>{t.play.notFound}</p>
        <a href={`#${paths.repertoire}`}>{t.play.back}</a>
      </div>
    )
  }
  return (
    <PlayView
      key={doc.revision.id}
      doc={doc}
      practice={practice}
      notice={notice}
      onNotice={setNotice}
    />
  )
}

interface PlayViewProps {
  doc: SongDocument
  practice: Practice | undefined
  notice: string | null
  onNotice: (message: string) => void
}

interface ActiveLoop {
  fragment: Fragment
  label: string
}

function PlayView({ doc, practice, notice, onNotice }: PlayViewProps) {
  const preferences = usePreferences()
  const { notation } = preferences
  const arrangementId = doc.arrangement.id
  const route = paths.play(arrangementId)
  const base = doc.revision.content

  const [resume] = useState(() => takeResumeDetail(route))
  const [shift, setShift] = useState(() => number(resume?.shift, 0))
  const [capo, setCapoTarget] = useState<number | null>(() =>
    typeof resume?.capo === 'number' ? resume.capo : null,
  )
  const [optionsOpen, setOptionsOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [awakeFailed, setAwakeFailed] = useState(false)
  const [activeLoop, setActiveLoop] = useState<ActiveLoop | null>(null)
  const [pendingA, setPendingA] = useState<EventRef | null>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const beatRef = useRef<HTMLSpanElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const scrollOnMove = useRef(false)

  const view = useMemo(() => {
    let content = base
    if (shift !== 0) content = transposeContent(content, shift)
    if (capo !== null) content = setCapo(content, capo)
    return content
  }, [base, shift, capo])

  const timeline = useMemo(() => buildEvents(view), [view])
  const { events } = timeline
  // Sin mapa de tiempos, la guía usa una estimación y lo dice (§5.1).
  const completed = useMemo(() => completeBeatMap(view), [view])
  const timing = useMemo(() => timingOf(completed.beatMap), [completed])
  const meter = useMemo(() => meterOf(view), [view])
  const links = useMemo(() => linkEvents(events, timing), [events, timing])
  const referenceBpm = view.tempo.reference?.bpm

  /** Pulso en que empieza un acorde de la hoja. */
  const startOf = useCallback(
    (sheetIndex: number) => {
      const timed = links.timedOfSheet[sheetIndex] ?? -1
      return timed >= 0 ? timing.events[timed]!.start : 0
    },
    [links, timing],
  )

  const [initialBeat] = useState(() =>
    startOf(Math.min(Math.max(number(resume?.position, 0), 0), Math.max(events.length - 1, 0))),
  )

  const transport = useTransport({
    timing,
    beatsPerBar: meter.beatsPerBar,
    bpm: practice?.practiceBpm ?? referenceBpm ?? DEFAULT_BPM,
    initialBeat,
    metronome: preferences.metronome,
    beatDisplay: beatRef,
    onEnded: () => onNotice(t.transport.ended),
  })
  // Funciones estables del transporte: la hoja no se repinta entera en cada acorde.
  const { seek, start, pause, setBpm: setTransportBpm } = transport
  const running = transport.state.status === 'running'
  const bpm = transport.state.bpm

  const index =
    events.length === 0 || transport.index < 0 ? -1 : (links.sheetOfTimed[transport.index] ?? -1)
  const current = index >= 0 ? events[index] : undefined
  const show = useCallback(
    (symbol: string) => displayChord(symbol, notation, { unicode: true }),
    [notation],
  )
  const next = index >= 0 ? nextDistinct(events, index, show) : undefined
  const marker: SheetPosition | null = current
    ? { occurrenceId: current.occurrenceId, lineId: current.lineId, chordId: current.chordId }
    : null

  const occurrenceStarts = useMemo(() => firstEventOfOccurrences(events), [events])

  // Sigue en el mismo acorde y en el mismo tono si iPadOS cierra la app (§9.5).
  useEffect(() => {
    saveDetail({ position: index, shift, capo })
  }, [index, shift, capo])

  const moveTo = useCallback(
    (target: number, scroll: boolean) => {
      scrollOnMove.current = scroll
      seek(startOf(target))
    },
    [seek, startOf],
  )

  // Al moverse con el pedal, y sonando cuando cambia la línea, la línea actual se centra.
  const lastLine = useRef<string | null>(null)
  useEffect(() => {
    if (!current) return
    const line = `${current.occurrenceId}:${current.lineId}`
    const changed = line !== lastLine.current
    lastLine.current = line
    if (!scrollOnMove.current && !(running && changed)) return
    scrollOnMove.current = false
    const element = sheetRef.current?.querySelector(`[data-line="${line}"]`)
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [current, running])

  // La barra de la guía está fija abajo: la consola y la columna de acordes se apartan.
  useEffect(() => {
    const element = barRef.current
    if (!element) return
    const root = window.document.documentElement
    const update = () => root.style.setProperty('--bottom-bar', `${element.offsetHeight}px`)
    update()
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null
    observer?.observe(element)
    window.addEventListener('resize', update)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', update)
      root.style.removeProperty('--bottom-bar')
    }
  }, [])

  const savePractice = useCallback(
    (patch: PracticePatch) => {
      updatePractice(getDb(), arrangementId, patch, new Date().toISOString()).catch(
        (cause: unknown) => onNotice(cause instanceof Error ? cause.message : String(cause)),
      )
    },
    [arrangementId, onNotice],
  )

  const togglePlay = useCallback(() => {
    if (events.length === 0) return
    if (running) {
      pause()
      return
    }
    // Se entra siempre al principio del acorde marcado, tras la cuenta.
    start({
      from: startOf(Math.max(index, 0)),
      countIn: preferences.countInBars * meter.beatsPerBar,
    })
  }, [events.length, running, pause, start, startOf, index, preferences.countInBars, meter])

  const changeBpm = useCallback(
    (value: number) => {
      setTransportBpm(value)
      savePractice({ practiceBpm: value })
    },
    [setTransportBpm, savePractice],
  )

  const perform = useCallback(
    (action: PedalAction) => {
      if (events.length === 0) return
      const last = events.length - 1
      const here = Math.max(index, 0)
      switch (action) {
        case 'next':
          moveTo(Math.min(here + 1, last), true)
          break
        case 'previous':
          moveTo(Math.max(here - 1, 0), true)
          break
        case 'nextSection':
          moveTo(jumpSection(events, here, 1), true)
          break
        case 'previousSection':
          moveTo(jumpSection(events, here, -1), true)
          break
        case 'playPause':
          togglePlay()
          break
      }
    },
    [events, index, moveTo, togglePlay],
  )

  // Teclado y pedal Bluetooth (llega como teclado). Las teclas se configuran en Ajustes.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return
      const action = actionForKey(preferences, keyId(event))
      if (!action) return
      event.preventDefault()
      perform(action)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [preferences, perform])

  // Pantalla encendida: Wake Lock donde existe y, en el iPad, un vídeo con pista silenciosa. En
  // iOS solo arranca desde un gesto, así que se intenta con los primeros toques o teclas.
  useEffect(() => {
    if (!preferences.keepAwake) return
    const gestures = ['click', 'touchend', 'keydown'] as const
    let busy = false
    let failures = 0
    const stopListening = () =>
      gestures.forEach((name) => window.removeEventListener(name, attempt, true))
    function attempt() {
      if (busy) return
      busy = true
      startKeepAwake('auto').then(
        () => stopListening(),
        (error: unknown) => {
          busy = false
          failures += 1
          console.warn('No se pudo activar la pantalla encendida', error)
          if (failures >= 3) setAwakeFailed(true)
        },
      )
    }
    gestures.forEach((name) => window.addEventListener(name, attempt, true))
    return () => {
      stopListening()
      stopKeepAwake()
    }
  }, [preferences.keepAwake])

  const awake = useSyncExternalStore(subscribeKeepAwake, keepAwakeMethod)

  const onPick = useCallback(
    (occurrenceId: string, lineId: string, chordId: string | null) => {
      const target = indexForPick(timeline, occurrenceId, lineId, chordId)
      if (target >= 0) moveTo(target, false)
    },
    [timeline, moveTo],
  )

  // Bucles: apuntan a acordes (IDs), y el pulso se calcula con el mapa de tiempos (regla 3).
  const symbolOf = useMemo(
    () => new Map(events.map((event) => [`${event.occurrenceId}:${event.chordId}`, event.symbol])),
    [events],
  )
  const timedOf = useCallback(
    (ref: EventRef) =>
      timing.events.find(
        (event) => event.occurrence === ref.occurrence && event.chord === ref.chord,
      ),
    [timing],
  )
  const loopOf = useCallback(
    (fragment: Fragment): Loop | null => {
      const from = timedOf(fragment.from)
      const to = timedOf(fragment.to)
      if (!from || !to) return null
      return normalizeLoop({
        start: Math.min(from.start, to.start),
        end: Math.max(from.start + from.beats, to.start + to.beats),
      })
    },
    [timedOf],
  )
  const fragmentLabel = useCallback(
    (fragment: Fragment): string => {
      const chords = timing.events.filter(
        (event) => event.occurrence === fragment.from.occurrence && event.chord !== null,
      )
      const whole =
        fragment.from.occurrence === fragment.to.occurrence &&
        chords[0]?.chord === fragment.from.chord &&
        chords[chords.length - 1]?.chord === fragment.to.chord
      const occurrence = view.sequence.find((item) => item.id === fragment.from.occurrence)
      const section = view.sections.find((item) => item.id === occurrence?.section)
      if (whole && section) return sectionJumpLabel(section, show)
      const symbol = (ref: EventRef) => show(symbolOf.get(`${ref.occurrence}:${ref.chord}`) ?? '?')
      return `${symbol(fragment.from)} → ${symbol(fragment.to)}`
    },
    [timing, view, show, symbolOf],
  )

  const applyFragment = (fragment: Fragment) => {
    const loop = loopOf(fragment)
    if (!loop) return
    transport.setLoop(loop)
    if (transport.beat < loop.start || transport.beat >= loop.end) seek(loop.start)
    setActiveLoop({ fragment, label: fragmentLabel(fragment) })
    setPendingA(null)
    savePractice({ fragment })
  }

  const currentRef: EventRef | null = current
    ? { occurrence: current.occurrenceId, chord: current.chordId }
    : null

  const loopSection = () => {
    if (!current) return
    const chords = timing.events.filter(
      (event) => event.occurrence === current.occurrenceId && event.chord !== null,
    )
    const first = chords[0]
    const last = chords[chords.length - 1]
    if (!first || !last) return
    applyFragment({
      from: { occurrence: first.occurrence, chord: first.chord! },
      to: { occurrence: last.occurrence, chord: last.chord! },
    })
  }

  const loopAB = () => {
    if (!currentRef) return
    if (!pendingA) {
      setPendingA(currentRef)
      return
    }
    const a = timedOf(pendingA)
    const b = timedOf(currentRef)
    if (!a || !b) return
    applyFragment(
      a.start <= b.start ? { from: pendingA, to: currentRef } : { from: currentRef, to: pendingA },
    )
  }

  const clearLoop = () => {
    transport.setLoop(null)
    setActiveLoop(null)
    setPendingA(null)
  }

  const savedFragment =
    practice?.fragment && !activeLoop && loopOf(practice.fragment) ? practice.fragment : null

  // Velocidad progresiva: «me sale» anota el tempo cómodo y sube unos BPM (§5.3).
  const comfortable = () => {
    const nextBpm = Math.min(MAX_BPM, bpm + preferences.speedStep)
    setTransportBpm(nextBpm)
    savePractice({ comfortableBpm: bpm, practiceBpm: nextBpm })
    onNotice(t.transport.comfortableSaved(bpm, nextBpm))
  }

  const resetTune = () => {
    setShift(0)
    setCapoTarget(null)
  }

  async function saveTune() {
    setSaving(true)
    try {
      const parts = [
        shift !== 0 ? `Transpuesta ${t.play.semitones(shift)}` : null,
        view.capo !== base.capo ? `cejilla ${view.capo}` : null,
      ].filter(Boolean)
      await saveRevision(getDb(), arrangementId, view, {
        now: new Date().toISOString(),
        reason: parts.join(' · ') || 'Tono',
      })
      onNotice(t.play.tuneSaved)
    } catch (cause) {
      onNotice(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSaving(false)
    }
  }

  async function accept() {
    try {
      await saveRevision(getDb(), arrangementId, acceptReview(base), {
        now: new Date().toISOString(),
        reason: 'Revisada',
      })
    } catch (cause) {
      onNotice(cause instanceof Error ? cause.message : String(cause))
    }
  }

  const resize = (delta: number) =>
    updatePreferences((currentPreferences) => ({
      ...currentPreferences,
      fontSize: Math.min(MAX_SIZE, Math.max(MIN_SIZE, currentPreferences.fontSize + delta)),
    }))

  const p = t.play
  const key = shapeKeyOf(view)
  const sheetStyle = { '--sheet-size': `${preferences.fontSize}px` } as CSSProperties
  const themes: ['dark' | 'light' | 'contrast', string][] = [
    ['dark', p.themeDark],
    ['light', p.themeLight],
    ['contrast', p.themeContrast],
  ]
  const timingReviewed = !completed.fromRule && completed.beatMap.status === 'reviewed'

  return (
    <div className={styles.play} style={sheetStyle}>
      <header className={styles.bar}>
        <a href={`#${paths.repertoire}`}>
          <button>← {p.back}</button>
        </a>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>
            {doc.song.title}
            {doc.song.artist && <span className={styles.artist}> · {doc.song.artist}</span>}
          </h1>
          <div className={styles.meta}>
            {key && (
              <span className={common.badge}>
                {isKeyEstimated(base)
                  ? p.keyEstimated(formatKey(key, notation, { unicode: true }))
                  : p.key(formatKey(key, notation, { unicode: true }))}
              </span>
            )}
            {view.capo > 0 && <span className={common.badge}>{p.capo(view.capo)}</span>}
            {referenceBpm !== undefined && (
              <span className={common.badge}>{p.bpm(referenceBpm)}</span>
            )}
            {meter.assumed ? (
              <span className={common.badgeWarn}>
                {t.transport.meterAssumed(meter.timeSignature)}
              </span>
            ) : (
              <span className={common.badge}>{meter.timeSignature}</span>
            )}
            {events.length > 0 && (
              <span className={timingReviewed ? common.badge : common.badgeWarn}>
                {timingReviewed ? t.transport.timingReviewed : t.transport.timingEstimated}
              </span>
            )}
            {(shift !== 0 || capo !== null) && (
              <span className={common.badgeWarn}>{p.tuneTitle}</span>
            )}
          </div>
        </div>
        <div className={styles.controls}>
          <button aria-label={`${p.fontSize} −`} onClick={() => resize(-2)}>
            {p.smaller}
          </button>
          <button aria-label={`${p.fontSize} +`} onClick={() => resize(2)}>
            {p.larger}
          </button>
          <button
            aria-expanded={optionsOpen}
            className={optionsOpen ? common.toggleOn : undefined}
            onClick={() => setOptionsOpen((open) => !open)}
          >
            {p.options}
          </button>
          <a href={`#${paths.timing(arrangementId)}`}>
            <button>{t.transport.adjustTiming}</button>
          </a>
          <a href={`#${paths.edit(arrangementId)}`}>
            <button>{p.edit}</button>
          </a>
        </div>
      </header>

      {optionsOpen && (
        <section className={styles.options} aria-label={p.options}>
          <div>
            <h2>{p.notation}</h2>
            <div className={styles.group}>
              {(['american', 'latin'] as const).map((value) => (
                <button
                  key={value}
                  aria-pressed={notation === value}
                  className={notation === value ? common.toggleOn : undefined}
                  onClick={() => updatePreferences((current) => ({ ...current, notation: value }))}
                >
                  {value === 'american' ? p.notationAmerican : p.notationLatin}
                </button>
              ))}
            </div>
            <h2 style={{ marginTop: 14 }}>{p.theme}</h2>
            <div className={styles.group}>
              {themes.map(([value, label]) => (
                <button
                  key={value}
                  aria-pressed={preferences.theme === value}
                  className={preferences.theme === value ? common.toggleOn : undefined}
                  onClick={() => updatePreferences((current) => ({ ...current, theme: value }))}
                >
                  {label}
                </button>
              ))}
            </div>
            <h2 style={{ marginTop: 14 }}>{p.awake}</h2>
            <div className={styles.group}>
              <button
                aria-pressed={preferences.keepAwake}
                className={preferences.keepAwake ? common.toggleOn : undefined}
                onClick={() =>
                  updatePreferences((current) => ({ ...current, keepAwake: !current.keepAwake }))
                }
              >
                {preferences.keepAwake ? p.awakeOn : p.awakeOff}
              </button>
              {awake && (
                <span className={common.badge}>
                  {awake === 'wake-lock' ? 'Wake Lock' : 'Vídeo'}
                </span>
              )}
            </div>
            {awakeFailed && <p className={styles.hint}>{p.awakeFailed}</p>}
          </div>
          <TunePanel
            base={base}
            view={view}
            shift={shift}
            notation={notation}
            saving={saving}
            onShift={setShift}
            onCapo={setCapoTarget}
            onReset={resetTune}
            onSave={() => void saveTune()}
          />
          <PracticePanel practice={practice} onChange={savePractice} />
        </section>
      )}

      {needsReview(base) && (
        <p className={styles.banner} role="status">
          <span>{p.review}</span>
          <button onClick={() => void accept()}>{p.reviewAccept}</button>
        </p>
      )}
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}

      <div className={styles.layout}>
        <aside className={styles.panel}>
          <ChordCard label={p.now} symbol={current?.symbol} notation={notation} main />
          <ChordCard label={p.next} symbol={next?.symbol} notation={notation} />
          {occurrenceStarts.size > 1 && (
            <>
              <div className={styles.jumpsLabel}>{p.sections}</div>
              <div className={styles.jumps} role="group" aria-label={p.sections}>
                {view.sequence.map((occurrence) => {
                  const start = occurrenceStarts.get(occurrence.id)
                  const section = view.sections.find((item) => item.id === occurrence.section)
                  if (start === undefined || !section) return null
                  return (
                    <button key={occurrence.id} onClick={() => moveTo(start, true)}>
                      {sectionJumpLabel(section, show)}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </aside>

        <div ref={sheetRef} className={styles.sheetWrap}>
          <Sheet content={view} notation={notation} current={marker} onPick={onPick} />
          {view.notes && <p className={styles.keysHint}>{view.notes}</p>}
          <p className={styles.keysHint}>{p.moveHint}</p>
        </div>
      </div>

      {transport.phase === 'countIn' && transport.countInLeft > 0 && (
        <div className={transportStyles.countIn} aria-hidden="true">
          {transport.countInLeft}
        </div>
      )}

      <TransportBar
        phase={transport.phase}
        bpm={bpm}
        referenceBpm={referenceBpm}
        metronome={preferences.metronome}
        speedStep={preferences.speedStep}
        loopLabel={activeLoop?.label ?? null}
        pickingEnd={pendingA !== null}
        fragmentLabel={savedFragment ? fragmentLabel(savedFragment) : null}
        disabled={events.length === 0}
        beatRef={beatRef}
        barRef={barRef}
        onPlay={togglePlay}
        onPause={togglePlay}
        onStop={transport.stop}
        onBpm={changeBpm}
        onMetronome={() =>
          updatePreferences((current) => ({ ...current, metronome: !current.metronome }))
        }
        onLoopSection={loopSection}
        onLoopAB={loopAB}
        onClearLoop={clearLoop}
        onFragment={() => savedFragment && applyFragment(savedFragment)}
        onComfortable={comfortable}
      />
    </div>
  )
}
